import React from 'react';
import {
    DataCollectionGapSummaryMetadata,
    SupplierGapDocumentUploadDetails
} from '../../../../store/Models/Api/DataCollectionGapSummaryModels';
import { LoadingSpinner, LoadingSpinnerSize } from '../../LoadingSpinner';
import GapResolutionSection from './GapResolutionSection';
import GapSummarySection from './GapSummarySection';
import QuestionSummarySection from './QuestionSummarySection';
import SupplierSubmittedFilesPanel from './SupplierSubmittedFilesPanel';
import { useGapSummaryData } from './hooks/useGapSummaryData';
import './AIGapSummary.css';

enum GapSummaryView {
    Summary,
    GapReport,
    SupplierFiles
}

interface AIGapSummaryProps {
    requestId?: string;
    operationId?: string;
    gapSummary?: DataCollectionGapSummaryMetadata | null;
    onStartOver?: () => void;
    onProcessSupplierDocuments?: (
        supplierFiles: SupplierGapDocumentUploadDetails[]
    ) => void;
}

export const AIGapSummary: React.FC<AIGapSummaryProps> = ({
    requestId: requestIdProp,
    operationId: operationIdProp,
    gapSummary,
    onStartOver,
    onProcessSupplierDocuments
}) => {
    const requestId = requestIdProp ?? '';
    const operationId = gapSummary?.operationId ?? operationIdProp ?? '';
    const [currentView, setCurrentView] = React.useState(
        gapSummary?.dateSent ? GapSummaryView.SupplierFiles : GapSummaryView.Summary);
    const updateViewForSummary = React.useCallback((
        loadedSummary: DataCollectionGapSummaryMetadata
    ) => {
        setCurrentView(current =>
            loadedSummary.dateSent
                ? GapSummaryView.SupplierFiles
                : current === GapSummaryView.SupplierFiles
                    ? GapSummaryView.Summary
                    : current);
    }, []);
    const {
        summary,
        isLoading,
        errorMessage,
        setErrorMessage,
        markGapReportSent
    } = useGapSummaryData({
        requestId,
        operationId,
        sourceGapSummary: gapSummary,
        onSummaryLoaded: updateViewForSummary
    });

    React.useEffect(() => {
        setCurrentView(
            gapSummary?.requestId === requestId
                && gapSummary.operationId === operationId
                && gapSummary.dateSent
                ? GapSummaryView.SupplierFiles
                : GapSummaryView.Summary);
    }, [
        gapSummary?.dateSent,
        gapSummary?.operationId,
        gapSummary?.requestId,
        operationId,
        requestId
    ]);

    const handleEmailSent = async (recipientEmail: string): Promise<void> => {
        setCurrentView(GapSummaryView.SupplierFiles);
        await markGapReportSent(recipientEmail);
    };

    const renderContent = () => {
        if (isLoading) {
            return (
                <div className="gap-summary-loading">
                    <LoadingSpinner size={LoadingSpinnerSize.Large} />
                </div>
            );
        }

        if (!summary) {
            return (
                <div className="alert alert-warning">
                    {errorMessage}
                </div>
            );
        }

        if (currentView === GapSummaryView.SupplierFiles) {
            return (
                <>
                    {errorMessage && (
                        <div className="alert alert-danger" role="alert">
                            {errorMessage}
                        </div>
                    )}
                    <SupplierSubmittedFilesPanel
                        sentToEmail={summary.sentToEmail ?? ''}
                        supplierFiles={summary.supplierFiles ?? []}
                        dateResponseRecieved={summary.dateResponseRecieved}
                        onProcessSupplierDocuments={onProcessSupplierDocuments}
                        onBack={() => setCurrentView(GapSummaryView.Summary)}
                        setErrorMessage={setErrorMessage}
                    />
                </>
            );
        }

        return (
            <>
                {errorMessage && (
                    <div className="alert alert-danger" role="alert">
                        {errorMessage}
                    </div>
                )}

                {currentView === GapSummaryView.Summary && (
                    <GapSummarySection
                        summaryPayload={summary.summaryPayload}
                    />
                )}

                <GapResolutionSection
                    requestId={requestId}
                    operationId={summary.operationId}
                    summaryPayload={summary.summaryPayload}
                    showGapReport={currentView === GapSummaryView.GapReport}
                    onRequestMoreFiles={() => setCurrentView(
                        summary.dateSent
                            ? GapSummaryView.SupplierFiles
                            : GapSummaryView.GapReport)}
                    onBackToGapSummary={() => setCurrentView(GapSummaryView.Summary)}
                    onEmailSent={handleEmailSent}
                    onStartOver={onStartOver}
                    setErrorMessage={setErrorMessage}
                />

                {currentView === GapSummaryView.Summary && (
                    <QuestionSummarySection
                        summaryPayload={summary.summaryPayload}
                    />
                )}
            </>
        );
    };

    return (
        <div className={`gap-summary-root${currentView !== GapSummaryView.Summary
            ? ' gap-summary-root-supplier-input'
            : ''}`}>
            {renderContent()}
        </div>
    );
};

export default AIGapSummary;
